import * as qs from 'qs';
import { useMemo } from 'react';

import { useAddJobToNotificationQueue } from '../../../components/JobNotificationHub/Provider';
import { useReportDisplayName } from '../../../components/reportsv3/hooks/useReportDisplayName';
import { useReportOptionsParams } from '../../../components/reportsv3/hooks/useReportOptionsParams';
import { isReleaseGroupScope } from '../../../components/reportsv3/types';
import type { ReportsV3Params, ReportV3Format } from '../../../components/reportsv3/types';
import { useParams } from '../../../contexts/RouterContext';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { get } from '../../../redux/api';
import { useSelectedReleaseIdForRoute } from '../../../redux/project_grouping/hooks';
import { nuc } from '../../../utils';
import type {
  QueueReleaseGroupReportResponse,
  QueueRevisionReportResponse,
} from '../../../../shared/types/routes/queuedReports';

type QueuedReport = {
  jobToken: string;
  reportId: string;
};

type DownloadTarget = {
  scope: 'releaseGroup' | 'revision';
  url: string;
};

/**
 * Hook that returns a mutation to generate a report in the background for the user to
 * download, for reports too costly to render within a request (release group reports,
 * and project PDFs). The job is registered with the JobNotificationHub, whose completion
 * toast offers the download.
 *
 * @param format The report format.
 */
export function useQueueReportDownload(format: ReportV3Format) {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelectedReleaseIdForRoute();
  const reportOptionsParams = useReportOptionsParams();
  const addJob = useAddJobToNotificationQueue();
  const reportName = useReportDisplayName(format);

  const target = useMemo((): DownloadTarget | null => {
    if (!isReleaseGroup) {
      const revisionLocator = nuc(`${params.project_id}$${params.revision_id}`);
      return { scope: 'revision', url: `/api/v2/revisions/${revisionLocator}/attribution/email` };
    }
    const { projectGroupId, releaseId } = params;
    const resolvedReleaseId = releaseId ?? selectedReleaseId;
    if (resolvedReleaseId == null) {
      return null;
    }
    return {
      scope: 'releaseGroup',
      url: `/api/v2/project_group/${projectGroupId}/release/${resolvedReleaseId}/attribution/${format}`,
    };
  }, [format, isReleaseGroup, params, selectedReleaseId]);

  return useToastedMutation({
    mutationFn: async (): Promise<QueuedReport | null> => {
      if (!target) {
        throw new Error('Queued report downloads require a loaded report target.');
      }
      const query = { ...qs.parse(reportOptionsParams), format, delivery: 'download' };
      if (target.scope === 'revision') {
        const { data } = await get<QueueRevisionReportResponse>(target.url, false, query);
        return data ? { jobToken: data.jobToken, reportId: data.reportId } : null;
      }
      const { data } = await get<QueueReleaseGroupReportResponse>(target.url, false, query);
      return data ? { jobToken: String(data.taskId), reportId: data.reportId } : null;
    },
    onSuccess: (queued) => {
      if (queued) {
        addJob(queued.jobToken, {
          success: {
            content: `Your ${reportName} is ready to download.`,
            persistent: true,
            link: { text: 'Download', href: `/attribution/${queued.reportId}?download=true` },
          },
          failure: { content: `Couldn't generate your ${reportName}. Please try again.` },
        });
      }
    },
    successToastMessage: "Generating your report. We'll let you know when it's ready.",
    errorToastMessage: 'Failed to generate report',
  });
}
